import {
  useMutation,
  useQueryClient,
  UseQueryResult,
  useIsMutating,
} from "@tanstack/react-query";
import { clearJwtCookies } from "@/lib/jwt-shop-scope";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "@/hooks/useTranslation";

import {
  CountriesAndLanguages,
  CustomerAddress,
} from "@/networking/addresses.api";
import { useCustomerAddressesQuery } from "@/hooks/useCustomerAddresses";
import { useCustomerPaymentMethodsQuery } from "@/hooks/useCustomerPaymentMethods";
import {
  AvailablePaymentMethod,
  CartResponse,
  updateCartAttributes,
  applyPoints,
  removePoints,
  updateCartMetaData,
} from "@/networking/carts.api";
import { checkoutCart } from "@/networking/checkout.api";
import { logoutCustomer } from "@/networking/customers.api";
import {
  CustomerPaymentMethod,
  PaymentBehavior,
  updateCartPaymentMethod,
} from "@/networking/payments.api";
import { ShippingMethod } from "@/networking/shipping.api";

import { isApiError, type ApiError } from "@/lib/api-helpers";
import { pointsLabel, pluralPointsLabel } from "@/lib/points-label";
import { checkoutRedirect } from "@/lib/checkout-redirect";
import { sanitizeErrorMessage } from "@/lib/error-sanitizer";
import { flattenPerItemErrors } from "@/lib/flatten-per-item-errors";
import usePaypal from "@/components/checkout/hooks/usePaypal";
import { useAgreements } from "./hooks/useAgreements";
import { useHandleCheckout } from "./hooks/useHandleCheckout";
import { hasCartErrors, hasCartItemErrors } from "@/lib/api-helpers";
import {
  ErrorAlert,
  InfoAlert,
  MutedAlert,
  WarningAlert,
} from "@/components/ui/alert";
import { usePaymentMethodHandler } from "@/hooks/usePaymentMethodHandler";
import type { CheckoutFormData } from "@/components/checkout/checkout.types";
import CustomerPaymentMethods from "@/components/checkout/customer-payment-methods";
import CustomerShippingAddresses from "@/components/checkout/customer-shipping-addresses";
import { useCartMutations } from "@/components/checkout/hooks/useCartMutations";
import useCartCountry from "@/components/checkout/hooks/useCartCountry";
import ShippingMethodSelect from "@/components/checkout/shipping-method-select";
import Agreements from "@/components/checkout/agreements";
import OptionalAgreements from "@/components/checkout/optional-agreements";
import Footer from "@/components/checkout/footer";

import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn, DecodedJWT, getPaymentBehavior, splitName } from "@/lib/utils";
import VolumeCard from "@/components/checkout/volume-card";
import DropZone from "@/components/drop-zones/drop-zone";
import { getBrowserInfo } from "@fluid-app/fluid-pay-core/utils/browser-info";
import { renderFingerprintingIframe } from "@fluid-app/fluid-pay-core/utils/fingerprinting";
import FastCheckoutOptions from "./fast-checkout-options";
import FluidPoints from "@/components/checkout/fluid-points";
import { usePaymentMethodScript } from "@/hooks/usePaymentMethodScript";
import { PaymentMethodData } from "@/networking/checkout.api";
import MarketingCheckboxes from "./marketing-checkboxes";
import useBraintreePayment from "./hooks/useBraintreePayment";
import { useBraintree } from "@/components/checkout/braintree-provider";
import useSyncCustomer from "./hooks/useSyncCustomer";

// VGS Imports
import {
  cancelVgs3DS,
  checkCartVgs3DSStatus,
  initializeVgs3DS,
} from "@/networking/three-ds/vgs.api";
import { useVgs3DSCompletion, VgsThreeDSModal } from "@fluid-app/fluid-pay-ui";
import BlikOtpModal from "./blik-otp-modal";
import { pusherClient } from "@/lib/pusher/pusher-client";
import { useLoadingOverlay } from "../providers/CheckoutLoading";

type CartPaymentMethod = NonNullable<CartResponse["cart"]["payment_method"]>;
type PaymentMethodSelection = {
  paymentMethodId: number;
  requestId: number;
};

function cartPaymentMethodFromCustomerPaymentMethod(
  paymentMethod: CustomerPaymentMethod,
): CartPaymentMethod {
  const expMonth = paymentMethod.details.exp_month;
  const expYear = paymentMethod.details.exp_year;

  return {
    id: paymentMethod.id,
    type: paymentMethod.payment_type,
    payment_method_type:
      paymentMethod.details.card_brand ??
      paymentMethod.details.card_network ??
      paymentMethod.details.type ??
      null,
    last_four: paymentMethod.details.last_four ?? null,
    exp_month: expMonth == null ? null : String(expMonth),
    exp_year: expYear == null ? null : String(expYear),
    logo_url: paymentMethod.details.logo_url,
  };
}

export default function LoggedInCheckout({
  query,
  checkoutForm,
  cartToken,
  jwt,
  decodedJwt,
  affirmPublicKey,
  paymentMethodsIncludes,
  scrollRef,
  kountSessionId,
  sdkType,
  braintreePaymentAccountId: _braintreePaymentAccountId,
  countries,
}: {
  query: UseQueryResult<CartResponse | null>;
  checkoutForm: UseFormReturn<CheckoutFormData>;
  cartToken: string;
  jwt: string | null;
  decodedJwt: DecodedJWT | null;
  affirmPublicKey: string | undefined;
  paymentMethodsIncludes: string | null;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  kountSessionId?: string;
  sdkType?: string;
  braintreePaymentAccountId?: string;
  countries: CountriesAndLanguages["countries"];
}) {
  const { control } = checkoutForm;
  const router = useRouter();
  const { t, language } = useTranslation();
  const { cart } = query.data || {};
  const { subdomain } = query.data?.cart?.company || {};
  const queryClient = useQueryClient();
  const cartQueryKey = useMemo(
    () => ["cart", cartToken, paymentMethodsIncludes] as const,
    [cartToken, paymentMethodsIncludes],
  );
  const latestPaymentMethodSelectionRef = useRef<PaymentMethodSelection | null>(
    null,
  );
  const nextPaymentMethodSelectionRequestIdRef = useRef(0);
  const { showCheckoutLoader, hideCheckoutLoader } = useLoadingOverlay();

  const isSubscriptionPending =
    useIsMutating({
      mutationKey: ["update-item-subscription", cartToken],
    }) > 0;
  const isQuantityPending =
    useIsMutating({
      mutationKey: ["update-item-quantity", cartToken],
    }) > 0;
  const isDiscountPending =
    useIsMutating({
      mutationKey: ["apply-discount", cartToken],
    }) > 0;
  const isRemoveDiscountPending =
    useIsMutating({
      mutationKey: ["remove-discount", cartToken],
    }) > 0;
  // Gate for the valid_for_checkout_pre_payment fallback banner: keyed
  // cart mutations must include cartToken; keyless mutations are accepted
  // because the regression motivator (updateCartPaymentMethod) is keyless.
  // This also matches keyless non-cart mutations like fetchAgreement, but
  // briefly hiding the fallback during one of those is harmless.
  const anyCartMutationPending =
    useIsMutating({
      predicate: (mutation) => {
        const mutationKey = mutation.options.mutationKey;
        if (!mutationKey) return true;
        return mutationKey.includes(cartToken);
      },
    }) > 0;
  const [shippingMethodAccordionValue, setShippingMethodAccordionValue] =
    useState<string>("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | undefined>(
    undefined,
  );
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [addressError, setAddressError] = useState<string | undefined>(
    undefined,
  );
  const [shippingMethodError, setShippingMethodError] = useState<
    string | undefined
  >(undefined);
  const [paymentError, setPaymentError] = useState<string | undefined>(
    undefined,
  );
  const [selectedAlternatePaymentMethod, setSelectedAlternatePaymentMethod] =
    useState<AvailablePaymentMethod | null>(null);
  const [customerDetails, setCustomerDetails] = useState<Record<
    string,
    string
  > | null>(null);
  const [customerDetailsValidationErrors, setCustomerDetailsValidationErrors] =
    useState<Record<string, string[]>>({});
  const [emailMarketing, setEmailMarketing] = useState(true);
  const [smsMarketing, setSmsMarketing] = useState(true);

  // VGS 3DS modal state
  const [vgsThreeDSModal, setVgsThreeDSModal] = useState<{
    isOpen: boolean;
    challengeHtml: string;
    challengeUrl: string;
    transactionId: string;
    merchantTxId: string;
  }>({
    isOpen: false,
    challengeHtml: "",
    challengeUrl: "",
    transactionId: "",
    merchantTxId: "",
  });

  // BLIK OTP modal state
  const [blikOtpModal, setBlikOtpModal] = useState<{ isOpen: boolean }>({
    isOpen: false,
  });

  const selectedShippingAddressId = query.data?.cart.ship_to?.id;
  const selectedPaymentMethodId = query.data?.cart.payment_method?.id;
  const alternatePaymentMethods =
    query.data?.cart.available_payment_methods.filter(
      (method) =>
        getPaymentBehavior(method.type) !== PaymentBehavior.CREDIT_CARD,
    );
  const { addAddressToCartMutation, updateShippingMethodMutation } =
    useCartMutations(cartToken, paymentMethodsIncludes);
  const { handleCountryChange, isUpdatingCountry } = useCartCountry(
    cartToken,
    paymentMethodsIncludes,
    undefined,
    countries,
  );

  const { getPaymentMethodData } = usePaymentMethodScript(
    query.data?.cart?.available_payment_methods,
    selectedAlternatePaymentMethod?.type,
  );

  const {
    requiredAgreements,
    optionalCheckboxAgreements,
    footerAgreements,
    setOptionalCheckedIds,
    getAcceptedAgreementIds,
  } = useAgreements(query.data?.cart?.agreements || []);
  // Memoize so handleBraintreeSuccess (which lists this in its
  // useCallback deps) only rebuilds when the underlying agreement IDs
  // actually change. getAcceptedAgreementIds returns a fresh array
  // every call, so without this every parent re-render would churn the
  // Braintree onSuccess prop.
  const acceptedAgreementIds = useMemo(
    () => getAcceptedAgreementIds(),
    [getAcceptedAgreementIds],
  );
  // Drives the cart-metadata write for accepted_agreement_ids. We write
  // whenever the cart has any checkout agreements (not just when the
  // user selected something) so an empty current selection explicitly
  // overwrites any stale value left by a prior submission.
  const hasCheckoutAgreements = (query.data?.cart?.agreements?.length ?? 0) > 0;

  // Hoisted above handleBraintreeSuccess so the callback closure can
  // reference the mutation handle when persisting accepted_agreement_ids.
  const updateCartMetaDataMutation = useMutation({
    mutationFn: updateCartMetaData,
  });

  // Reset customer details and clear errors when payment method changes
  useEffect(() => {
    setCustomerDetailsValidationErrors({});
    setPaymentError(undefined);
    setCustomerDetails(null);
  }, [selectedAlternatePaymentMethod]);

  // clear local state errors on language change
  useEffect(() => {
    setCheckoutError(undefined);
    setEmailError(undefined);
    setAddressError(undefined);
    setShippingMethodError(undefined);
    setPaymentError(undefined);
    setCustomerDetailsValidationErrors({});
  }, [language]);

  const customerDetailsInitialValues = useMemo(() => {
    const cartPaymentMethod = query.data?.cart.payment_method;
    if (
      !cartPaymentMethod?.customer_details ||
      !selectedAlternatePaymentMethod ||
      cartPaymentMethod.type !== selectedAlternatePaymentMethod.type
    ) {
      return undefined;
    }

    const details = cartPaymentMethod.customer_details;
    if (typeof details === "object" && details !== null) {
      const result: Record<string, string> = {};
      Object.entries(details).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          result[key] = String(value);
        }
      });
      return Object.keys(result).length > 0 ? result : undefined;
    }
    return undefined;
  }, [query.data?.cart.payment_method, selectedAlternatePaymentMethod]);

  // Use the dynamic payment method handler
  const { handlePaymentMethod, isPaymentLoading } =
    usePaymentMethodHandler(cartToken);

  const handlePaypalSuccess = useCallback(async () => {
    // Optional: any success handling later
  }, []);

  const handlePaypalError = useCallback((error: Error) => {
    console.error("PayPal payment failed:", error);
    setPaymentError(error.message);
  }, []);

  const { paypalButton, venmoButton, payLaterButton } = usePaypal({
    fastCheckout: false,
    cartToken,
    onSuccess: handlePaypalSuccess,
    onError: handlePaypalError,
    paymentMethodsIncludes,
    vaultWithoutCharge: cart?.vault_without_charge ?? false,
  });

  const handleBraintreeSuccess = useCallback(
    async (_result: { transaction_id: string; payment_type: string }) => {
      try {
        showCheckoutLoader("Placing your order...");

        // Save shipping address to cart (mirrors handleSubmitPayment step 3)
        const addressId = selectedShippingAddressId?.toString();
        if (addressId && jwt) {
          const cartResult = await addAddressToCartMutation.mutateAsync({
            type: "ship_to",
            jwt,
            address_id: addressId,
          });

          // Auto-select first shipping method if none selected
          if (
            !cartResult.cart.digital_only &&
            !cartResult.cart.shipping_method
          ) {
            const firstMethod = cartResult.cart.available_shipping_methods?.[0];
            if (firstMethod) {
              await updateShippingMethodMutation.mutateAsync({
                shippingMethodId: firstMethod.id.toString(),
              });
            }
          }
        }

        // Persist accepted_agreement_ids before the first checkout call
        // — the Braintree PayPal/Venmo path bypasses handleSubmitPayment
        // (its own button replaces the submit button), so without this
        // the metadata write that the regular submit flow does never
        // happens, and the pre-purchase finalize would lose the
        // optional-checked IDs.
        if (hasCheckoutAgreements) {
          await updateCartMetaDataMutation.mutateAsync({
            cartToken,
            metaData: { accepted_agreement_ids: acceptedAgreementIds },
          });
        }

        const checkoutResult = await checkoutCart(cartToken, {
          acceptedAgreementIds,
        });
        checkoutRedirect(checkoutResult, cartToken);
      } catch (error) {
        hideCheckoutLoader();
        console.error("Braintree checkout error:", error);
        setPaymentError(
          error instanceof Error ? error.message : "Checkout failed",
        );
      }
    },
    [
      cartToken,
      router,
      showCheckoutLoader,
      hideCheckoutLoader,
      selectedShippingAddressId,
      jwt,
      addAddressToCartMutation,
      updateShippingMethodMutation,
      acceptedAgreementIds,
      hasCheckoutAgreements,
      updateCartMetaDataMutation,
    ],
  );

  const handleBraintreeError = useCallback((error: string) => {
    setPaymentError(sanitizeErrorMessage(error));
  }, []);

  const { deviceData } = useBraintree();

  const {
    paypalButton: braintreePaypalBtn,
    venmoButton: braintreeVenmoBtn,
    payLaterButton: braintreePayLaterBtn,
    payLaterMessage: braintreePayLaterMsg,
  } = useBraintreePayment({
    cartToken,
    onSuccess: handleBraintreeSuccess,
    onError: handleBraintreeError,
    paymentMethodsIncludes,
  });

  const paymentMethodAutoAssignable =
    query.data?.cart?.payment_method_auto_assignable ?? false;

  const requiresPaymentMethod =
    query.data?.cart?.requires_payment_method ?? true;

  useSyncCustomer(
    cartToken,
    paymentMethodsIncludes,
    jwt,
    decodedJwt,
    query.data ?? undefined,
  );

  const cartCountryIso = query.data?.cart?.country?.iso;

  // Use the canonical key from the hook so customer-payment-methods.tsx
  // and customer-shipping-addresses.tsx write to the same cache entry.
  const {
    data: customerPaymentMethods,
    isLoading: customerPaymentMethodsLoading,
  } = useCustomerPaymentMethodsQuery({
    countryCode: cartCountryIso,
    enabled: !!cartCountryIso,
  });

  const { data: customerAddresses, isLoading: customerAddressesLoading } =
    useCustomerAddressesQuery();

  const checkoutMutation = useMutation({
    mutationFn: ({
      cartToken,
      shouldIncludeBrowserInfo,
      kountSessionId,
      paymentMethodData,
      deviceData,
      acceptedAgreementIds,
    }: {
      cartToken: string;
      shouldIncludeBrowserInfo: boolean;
      // Marks the post-3DS retry from handleVgs3DSRedirectSuccess so
      // onError can reroute field errors to the checkout banner.
      isPostThreeDsRetry?: boolean;
      kountSessionId?: string;
      paymentMethodData?: PaymentMethodData;
      deviceData?: string;
      acceptedAgreementIds?: number[];
    }) => {
      const browserInfo = shouldIncludeBrowserInfo
        ? getBrowserInfo()
        : undefined;
      return checkoutCart(cartToken, {
        browserInfo,
        kountSessionId,
        paymentMethodData,
        deviceData,
        acceptedAgreementIds,
      });
    },
    onError: (error: unknown, variables) => {
      // Explicit marker set only by handleVgs3DSRedirectSuccess — decoupled
      // from shouldIncludeBrowserInfo so a future caller passing
      // shouldIncludeBrowserInfo: false for a non-3DS reason doesn't get its
      // errors rerouted to the checkout banner.
      const isPostThreeDsRetry = variables.isPostThreeDsRetry === true;
      hideCheckoutLoader();

      if (isApiError(error)) {
        console.error("Checkout error:", error);

        setEmailError(undefined);
        setAddressError(undefined);
        setShippingMethodError(undefined);
        setPaymentError(undefined);
        setCheckoutError(undefined);

        const errorsData = error?.data?.errors;

        // handle errors array with JSON strings (e.g. bill_to validation)
        if (Array.isArray(errorsData)) {
          const parsedErrors: string[] = [];
          errorsData.forEach((errStr) => {
            if (typeof errStr === "string") {
              try {
                const parsed = JSON.parse(errStr);
                Object.entries(parsed).forEach(([_section, fields]) => {
                  if (fields && typeof fields === "object") {
                    Object.entries(fields as Record<string, string[]>).forEach(
                      ([field, messages]) => {
                        if (Array.isArray(messages)) {
                          const fieldName = field
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (c) => c.toUpperCase());
                          parsedErrors.push(
                            `${fieldName} ${messages.join(", ")}`,
                          );
                        }
                      },
                    );
                  }
                });
              } catch {
                parsedErrors.push(errStr);
              }
            }
          });

          if (parsedErrors.length > 0) {
            setCheckoutError(parsedErrors.join(". "));
            return;
          }
        }

        // handle errors as object with attribute keys
        if (
          errorsData &&
          typeof errorsData === "object" &&
          !Array.isArray(errorsData)
        ) {
          for (const [attribute, messages] of Object.entries(errorsData)) {
            if (
              attribute &&
              messages &&
              Array.isArray(messages) &&
              messages.length > 0
            ) {
              const errorMessage = messages[0];

              switch (attribute) {
                case "email":
                  setEmailError(errorMessage);
                  break;
                case "address":
                case "shipping_address":
                  if (isPostThreeDsRetry) {
                    setCheckoutError(t("payment_verification_didnt_complete"));
                  } else {
                    setAddressError(errorMessage);
                  }
                  break;
                case "shipping_method":
                  setShippingMethodError(errorMessage);
                  break;
                case "payment_method":
                case "payment":
                case "payment_account":
                case "vgs":
                case "three_ds":
                  setPaymentError(sanitizeErrorMessage(errorMessage));
                  break;
                case "items": {
                  // Per-item error rows from the checkout orchestrator
                  // (e.g. `[{ id, customer_limit: ["..."] }]`). Pre-CURRENT-1487
                  // this branch fell through to the default String() formatter
                  // and rendered "Items [object Object]"; now flatten the
                  // inner string arrays into the checkout error banner.
                  const flattened = flattenPerItemErrors(messages);
                  if (flattened.length > 0) {
                    setCheckoutError(flattened.join(". "));
                  }
                  break;
                }
                case "bundle_inventory":
                case "bundle_child_items":
                case "bundle_configuration":
                case "bundle_customer_limit":
                case "country":
                case "cart":
                  setCheckoutError(errorMessage);
                  break;
                default:
                  setCheckoutError(
                    sanitizeErrorMessage(
                      errorMessage,
                      t("unable_to_process_checkout"),
                    ),
                  );
                  break;
              }
            }
          }
        } else if (error?.data?.error_message) {
          setCheckoutError(
            sanitizeErrorMessage(
              error.data.error_message,
              t("unable_to_process_checkout"),
            ),
          );
        } else {
          setCheckoutError(t("unable_to_process_checkout"));
        }
      } else {
        console.error("Unexpected error structure:", error);
        setCheckoutError(t("unable_to_process_checkout"));
      }
    },
  });

  const showCheckoutLoaderRef = useRef(showCheckoutLoader);
  const hideCheckoutLoaderRef = useRef(hideCheckoutLoader);
  const checkoutMutationRef = useRef(checkoutMutation);

  useEffect(() => {
    showCheckoutLoaderRef.current = showCheckoutLoader;
    hideCheckoutLoaderRef.current = hideCheckoutLoader;
    checkoutMutationRef.current = checkoutMutation;
  }, [showCheckoutLoader, hideCheckoutLoader, checkoutMutation]);

  const { refetch } = query;

  const handleVgs3DSRedirectSuccess = useCallback(async () => {
    showCheckoutLoaderRef.current("Placing your order...");

    try {
      const checkoutResult = await checkoutMutationRef.current.mutateAsync({
        cartToken,
        shouldIncludeBrowserInfo: false,
        isPostThreeDsRetry: true,
      });

      checkoutRedirect(checkoutResult, cartToken);
    } catch (error) {
      // Deliberately duplicates onError's hideCheckoutLoader: a throw from
      // checkoutRedirect (after a successful mutation) never reaches
      // onError, and this is then the only thing clearing the loader.
      hideCheckoutLoaderRef.current();
      console.error("Checkout after VGS 3DS redirect failed:", error);
      // Error display is handled by checkoutMutation.onError (see Task 6).
      // Refresh cart so UI reflects post-decline state (3DS metadata cleared,
      // cart reverted to payment_method) and the user can retry cleanly.
      refetch();
    }
  }, [cartToken, refetch]);

  useVgs3DSCompletion({
    channelName: `private-cart-${cartToken}`,
    transport: pusherClient,
    checkStatus: () => checkCartVgs3DSStatus(cartToken),
    onComplete: () => {
      setVgsThreeDSModal({
        isOpen: false,
        challengeHtml: "",
        challengeUrl: "",
        transactionId: "",
        merchantTxId: "",
      });
      handleVgs3DSRedirectSuccess();
    },
    onError: (error) => {
      setVgsThreeDSModal({
        isOpen: false,
        challengeHtml: "",
        challengeUrl: "",
        transactionId: "",
        merchantTxId: "",
      });
      hideCheckoutLoader();
      setPaymentError(sanitizeErrorMessage(error));
      // Refresh cart state so the UI reflects the backend state (e.g., cart
      // reverted to payment_method) and the user can safely retry.
      refetch();
    },
    enabled: vgsThreeDSModal.isOpen,
    merchantTxId: vgsThreeDSModal.merchantTxId,
    onCanceled: () => {
      setVgsThreeDSModal({
        isOpen: false,
        challengeHtml: "",
        challengeUrl: "",
        transactionId: "",
        merchantTxId: "",
      });
      hideCheckoutLoader();
      setCheckoutLoading(false);
      // No error surfaced: a cancel is neutral by design. Refresh so the UI
      // reflects the cart back in payment_method and the user can retry.
      refetch();
    },
  });

  const onSelectShippingMethod = (shippingMethod: ShippingMethod) => {
    updateShippingMethodMutation.mutate({
      shippingMethodId: shippingMethod.id,
    });
    setCheckoutError(undefined);
    setAddressError(undefined);
    setShippingMethodError(undefined);
  };

  const logoutCustomerMutation = useMutation({
    mutationFn: logoutCustomer,
    onSuccess: () => {
      clearJwtCookies();
      router.reload();
    },
  });

  const defaultShippingAddress = customerAddresses?.addresses?.filter(
    (address) => address.default,
  )[0];
  const defaultPaymentMethod = customerPaymentMethods?.payment_methods?.filter(
    (paymentMethod) => paymentMethod.default,
  )[0];

  const writeSelectedPaymentMethodToCartCache = useCallback(
    (paymentMethod: CustomerPaymentMethod) => {
      queryClient.setQueryData<CartResponse | null | undefined>(
        cartQueryKey,
        (cachedCartResponse) => {
          if (!cachedCartResponse?.cart) return cachedCartResponse;

          return {
            ...cachedCartResponse,
            cart: {
              ...cachedCartResponse.cart,
              payment_method:
                cartPaymentMethodFromCustomerPaymentMethod(paymentMethod),
            },
          };
        },
      );
    },
    [cartQueryKey, queryClient],
  );

  const isLatestPaymentMethodSelection = useCallback(
    (paymentMethod: CustomerPaymentMethod, requestId: number) =>
      latestPaymentMethodSelectionRef.current?.paymentMethodId ===
        paymentMethod.id &&
      latestPaymentMethodSelectionRef.current?.requestId === requestId,
    [],
  );
  const nextPaymentMethodSelectionRequestId = useCallback(() => {
    nextPaymentMethodSelectionRequestIdRef.current += 1;
    return nextPaymentMethodSelectionRequestIdRef.current;
  }, []);

  const updateCartPaymentMethodMutation = useMutation({
    mutationFn: async ({
      cartToken,
      jwt,
      paymentMethod,
      requestId,
    }: {
      cartToken: string;
      jwt: string;
      paymentMethod: CustomerPaymentMethod;
      requestId: number;
    }) => {
      const billingAddress = paymentMethod.billing_address
        ? {
            name: paymentMethod.billing_address.name || "",
            address1: paymentMethod.billing_address.address1 || "",
            address2: paymentMethod.billing_address.address2 || "",
            city: paymentMethod.billing_address.city || "",
            state: paymentMethod.billing_address.state || "",
            postal_code: paymentMethod.billing_address.zip || "",
            country_code: paymentMethod.billing_address.country_code || "",
          }
        : undefined;

      await updateCartPaymentMethod(
        cartToken,
        jwt,
        paymentMethod.id.toString(),
        billingAddress,
      );

      // Refetch cart within mutationFn so isPending stays true until the
      // cache has the updated bill_to from the backend. Previously the
      // refetch was fire-and-forget in onSuccess, which allowed the user
      // to submit checkout before the billing address was in the cache.
      // Swallow refetch errors — the backend write already succeeded, so
      // rolling back would desync FE/BE. Data self-heals on next read.
      await queryClient
        .refetchQueries({
          queryKey: cartQueryKey,
        })
        .catch(() => {});

      if (isLatestPaymentMethodSelection(paymentMethod, requestId)) {
        writeSelectedPaymentMethodToCartCache(paymentMethod);
      }
    },
    onMutate: async ({ paymentMethod, requestId }) => {
      latestPaymentMethodSelectionRef.current = {
        paymentMethodId: paymentMethod.id,
        requestId,
      };
      await queryClient.cancelQueries({ queryKey: cartQueryKey });
      const previousCart = queryClient.getQueryData<CartResponse | null>(
        cartQueryKey,
      );

      writeSelectedPaymentMethodToCartCache(paymentMethod);

      return { previousCart };
    },
    onSuccess: (_response, { paymentMethod, requestId }) => {
      if (!isLatestPaymentMethodSelection(paymentMethod, requestId)) return;

      setCheckoutError(undefined);
      setPaymentError(undefined);
    },
    onError: (error, { paymentMethod, requestId }, context) => {
      if (!isLatestPaymentMethodSelection(paymentMethod, requestId)) return;

      if (context?.previousCart !== undefined) {
        queryClient.setQueryData<CartResponse | null | undefined>(
          cartQueryKey,
          context.previousCart,
        );
      }
      let errorMessage = "Failed to update payment method";
      if (isApiError(error as unknown)) {
        const apiErr = error as ApiError;
        errorMessage =
          apiErr.data?.errors?.payment_method?.[0] ||
          sanitizeErrorMessage(apiErr.data?.error_message, errorMessage);
      }
      setPaymentError(errorMessage);
    },
  });

  const addEmailMutation = useMutation({
    mutationFn: updateCartAttributes,
  });

  const applyPointsMutation = useMutation({
    mutationFn: ({ pointsAmount }: { pointsAmount: number }) =>
      applyPoints(cartToken, pointsAmount),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cartQueryKey,
      });
      setCheckoutError(undefined);
    },
    onError: (error: unknown) => {
      console.error("Error applying points:", error);
      if (isApiError(error)) {
        const errorData = error?.data as
          | {
              errors?: { points?: string[]; cart?: string[] };
              error_message?: string;
            }
          | undefined;
        const errorMessage =
          errorData?.errors?.points?.[0] ||
          errorData?.errors?.cart?.[0] ||
          errorData?.error_message ||
          t("failed_to_apply_points", "Failed to apply ${label}", {
            label: pluralPointsLabel(cart?.company),
          });
        setCheckoutError(errorMessage);
      }
    },
  });

  const removePointsMutation = useMutation({
    mutationFn: () => removePoints(cartToken),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cartQueryKey,
      });
      setCheckoutError(undefined);
    },
    onError: (error: unknown) => {
      console.error("Error removing points:", error);
      if (isApiError(error)) {
        const errorData = error?.data as
          | {
              errors?: { points?: string[]; cart?: string[] };
              error_message?: string;
            }
          | undefined;
        const errorMessage =
          errorData?.errors?.points?.[0] ||
          errorData?.errors?.cart?.[0] ||
          errorData?.error_message ||
          t("failed_to_remove_points", "Failed to remove ${label}", {
            label: pluralPointsLabel(cart?.company),
          });
        setCheckoutError(errorMessage);
      }
    },
  });

  const handleApplyPoints = useCallback(
    async (points: number, _useForEntireSubtotal: boolean) => {
      await applyPointsMutation.mutateAsync({ pointsAmount: points });
    },
    [applyPointsMutation],
  );

  const handleRemovePoints = useCallback(async () => {
    await removePointsMutation.mutateAsync();
  }, [removePointsMutation]);

  const handleSelectShippingAddress = async (address: CustomerAddress) => {
    if (
      address.country_code &&
      address.country_code !== query.data?.cart?.country?.iso
    ) {
      await handleCountryChange(address.country_code);
    }
    try {
      await addAddressToCartMutation.mutateAsync({
        type: "ship_to",
        jwt: jwt as string,
        address_id: address.id.toString(),
      });
      setAddressError(undefined);
    } catch (error) {
      if (isApiError(error)) {
        const apiErr = error as ApiError;
        setAddressError(
          apiErr.data?.errors?.address?.[0] ||
            apiErr.data?.errors?.shipping_address?.[0] ||
            apiErr.data?.error_message ||
            t("failed_to_update_shipping_address"),
        );
      } else {
        setAddressError(t("failed_to_update_shipping_address"));
      }
      throw error;
    }
  };

  const handleSelectPaymentMethod = useCallback(
    async (paymentMethod: CustomerPaymentMethod) => {
      await updateCartPaymentMethodMutation.mutateAsync({
        cartToken,
        jwt: jwt as string,
        paymentMethod,
        requestId: nextPaymentMethodSelectionRequestId(),
      });
      setPaymentError(undefined);
    },
    [
      updateCartPaymentMethodMutation,
      cartToken,
      jwt,
      nextPaymentMethodSelectionRequestId,
    ],
  );

  const handleCheckout = useHandleCheckout(
    cartToken,
    checkoutMutation,
    setCheckoutLoading,
    setVgsThreeDSModal,
    kountSessionId,
    getPaymentMethodData,
    setBlikOtpModal,
    deviceData ?? undefined,
    acceptedAgreementIds,
  );

  const isAddressMutationLoading = addAddressToCartMutation.isPending;
  const isShippingMethodMutationLoading =
    updateShippingMethodMutation.isPending;
  const isPaymentMethodMutationLoading =
    updateCartPaymentMethodMutation.isPending;

  const handleSubmitPayment = async () => {
    setCheckoutLoading(true);

    const agreementsValue = checkoutForm.getValues(
      "customer.agreements_accepted",
    );

    if (requiredAgreements.length > 0 && !agreementsValue) {
      checkoutForm.setError("customer.agreements_accepted", {
        message: t("accept_terms_and_conditions_to_proceed"),
      });
      setCheckoutLoading(false);
      return;
    }
    if (
      !query.data?.cart?.digital_only &&
      !selectedShippingAddressId &&
      !defaultShippingAddress
    ) {
      setAddressError(t("shipping_address_is_required"));
      setCheckoutLoading(false);
      return;
    }

    if (
      requiresPaymentMethod &&
      !paymentMethodAutoAssignable &&
      !selectedPaymentMethodId &&
      !defaultPaymentMethod &&
      !selectedAlternatePaymentMethod
    ) {
      setPaymentError(t("payment_method_is_required"));
      setCheckoutLoading(false);
      return;
    }
    try {
      setEmailError(undefined);
      setAddressError(undefined);
      setPaymentError(undefined);
      setCheckoutError(undefined);
      setShippingMethodError(undefined);

      if (!query.data?.cart?.email && decodedJwt?.email) {
        try {
          await addEmailMutation.mutateAsync({
            cartToken,
            email: decodedJwt.email,
          });
        } catch (error) {
          console.error("Email error:", error);
          if (isApiError(error)) {
            const errorMessage =
              error?.data?.errors?.email?.[0] ||
              error?.data?.error_message ||
              t("failed_to_update_email");
            setEmailError(errorMessage);
          }
          throw error;
        }
      }

      const checkoutMetaData: Record<string, unknown> = {};
      if (cart?.company?.collect_email_marketing) {
        checkoutMetaData.email_marketing = emailMarketing;
      }
      if (cart?.company?.collect_sms_marketing) {
        checkoutMetaData.sms_marketing = smsMarketing;
      }
      // Persist accepted agreement IDs (required + optional-checked) on
      // the cart so a second checkoutCart call from PrePurchaseEnrollment
      // can read them back. Without this, the optional selections made
      // here are lost between the two-step finalize flow because they
      // live in this component's useState and don't survive navigation
      // to the enrollment form. Write whenever the cart has any
      // agreements at checkout so an empty current selection explicitly
      // overwrites any stale value (e.g. user unchecked an optional
      // they had ticked on a prior attempt).
      if (hasCheckoutAgreements) {
        checkoutMetaData.accepted_agreement_ids = acceptedAgreementIds;
      }
      if (Object.keys(checkoutMetaData).length > 0) {
        await updateCartMetaDataMutation.mutateAsync({
          cartToken,
          metaData: checkoutMetaData,
        });
      }

      if (!query.data?.cart?.digital_only) {
        try {
          await addAddressToCartMutation.mutateAsync({
            type: "ship_to",
            jwt: jwt as string,
            address_id:
              selectedShippingAddressId?.toString() ??
              defaultShippingAddress?.id.toString() ??
              "",
          });
        } catch (error) {
          console.error("Address error:", error);
          if (isApiError(error)) {
            const errorMessage =
              error?.data?.errors?.address?.[0] ||
              error?.data?.errors?.shipping_address?.[0] ||
              error?.data?.error_message ||
              t("failed_to_update_shipping_address");
            setAddressError(errorMessage);
          } else {
            setAddressError(t("failed_to_update_shipping_address"));
          }
          throw error;
        }
      }

      let activePaymentMethod: CustomerPaymentMethod | undefined;

      if (
        requiresPaymentMethod &&
        !paymentMethodAutoAssignable &&
        !selectedPaymentMethodId &&
        !selectedAlternatePaymentMethod &&
        defaultPaymentMethod
      ) {
        try {
          await updateCartPaymentMethodMutation.mutateAsync({
            cartToken,
            jwt: jwt as string,
            paymentMethod: defaultPaymentMethod,
            requestId: nextPaymentMethodSelectionRequestId(),
          });
          activePaymentMethod = defaultPaymentMethod;
        } catch (error) {
          console.error("Payment method error:", error);
          if (isApiError(error)) {
            const errorMessage =
              error?.data?.errors?.payment_method?.[0] ||
              error?.data?.errors?.payment?.[0] ||
              error?.data?.error_message ||
              "Failed to update payment method";
            setPaymentError(errorMessage);
          }
          throw error;
        }
      } else if (
        selectedPaymentMethodId &&
        customerPaymentMethods?.payment_methods
      ) {
        activePaymentMethod = customerPaymentMethods.payment_methods.find(
          (m) => m.id === selectedPaymentMethodId,
        );
      }

      if (paymentMethodAutoAssignable || !requiresPaymentMethod) {
        await handleCheckout();
        return;
      }

      if (selectedAlternatePaymentMethod) {
        const shippingAddress =
          query.data?.cart.ship_to || defaultShippingAddress;

        const useShippingForBillingValue = checkoutForm.getValues(
          "customer.payment_method.use_shipping_address_for_billing",
        );
        const billingAddressValue = checkoutForm.getValues(
          "customer.payment_method.billing_address",
        );

        // validate billing address if required
        if (
          selectedAlternatePaymentMethod.collect_billing_address &&
          !useShippingForBillingValue
        ) {
          let hasErrors = false;

          if (!billingAddressValue?.address1) {
            checkoutForm.setError(
              "customer.payment_method.billing_address.address1",
              {
                type: "manual",
              },
            );
            hasErrors = true;
          }
          if (!billingAddressValue?.city) {
            checkoutForm.setError(
              "customer.payment_method.billing_address.city",
              {
                type: "manual",
              },
            );
            hasErrors = true;
          }
          if (!billingAddressValue?.state) {
            checkoutForm.setError(
              "customer.payment_method.billing_address.state",
              {
                type: "manual",
              },
            );
            hasErrors = true;
          }
          if (!billingAddressValue?.postal_code) {
            checkoutForm.setError(
              "customer.payment_method.billing_address.postal_code",
              {
                type: "manual",
              },
            );
            hasErrors = true;
          }
          if (
            !billingAddressValue?.country &&
            !billingAddressValue?.country_code
          ) {
            checkoutForm.setError(
              "customer.payment_method.billing_address.country",
              {
                type: "manual",
              },
            );
            hasErrors = true;
          }

          if (hasErrors) {
            return;
          }
        }

        await handlePaymentMethod({
          paymentType: selectedAlternatePaymentMethod.type,
          onSuccess: handleCheckout,
          setPaymentError,
          customer_details: customerDetails || undefined,
          onCustomerDetailsValidationErrors: setCustomerDetailsValidationErrors,
          collectBillingAddress:
            selectedAlternatePaymentMethod.collect_billing_address ?? false,
          useShippingAddressForBilling: useShippingForBillingValue ?? false,
          additionalData: {
            cartData: query.data ?? null,
            affirmPublicKey,
            shippingInfo: query.data?.cart?.digital_only
              ? {
                  first_name: "",
                  last_name: "",
                  address1: "",
                  address2: "",
                  city: "",
                  state: "",
                  postal_code: "",
                  country: "",
                  country_code: "",
                  email: decodedJwt?.email ?? "",
                }
              : {
                  ...splitName(
                    query.data?.cart.ship_to?.name ||
                      defaultShippingAddress?.name ||
                      "",
                  ),
                  address1: shippingAddress?.address1 ?? "",
                  address2: shippingAddress?.address2 ?? "",
                  city: shippingAddress?.city ?? "",
                  state: shippingAddress?.state ?? "",
                  postal_code: shippingAddress?.postal_code ?? "",
                  country: query.data?.cart.country?.name ?? "",
                  country_code: shippingAddress?.country_code ?? "",
                  email: decodedJwt?.email ?? "",
                },
          },
        });
        return;
      }

      if (activePaymentMethod) {
        const { requires_3ds, vgs_card_id } = activePaymentMethod.details;
        const cartRequires3ds = query.data?.cart?.requires_3ds ?? false;

        if ((requires_3ds || cartRequires3ds) && vgs_card_id) {
          showCheckoutLoader("Initializing secure verification...");

          try {
            const initResult = await initializeVgs3DS({
              cartToken,
              vgsCardId: vgs_card_id,
            });

            if (initResult.three_ds_required === false) {
            } else if (initResult.fingerprinting_required === false) {
              // VGS 206: No fingerprinting needed, proceed directly
            } else if (initResult.tds_method_content) {
              await renderFingerprintingIframe(initResult.tds_method_content);
            }
          } catch (error) {
            console.error("VGS 3DS Initialization failed", error);
          }
          hideCheckoutLoader();
        }
      }

      await handleCheckout();
    } catch (error) {
      console.error("Error processing checkout:", error);
      // Only handle non-API errors here - API errors are handled by mutation onError callbacks
      if (!isApiError(error)) {
        setCheckoutError(t("unable_to_process_checkout"));
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const renderCollapsedShippingMethod = () => {
    if (query.isLoading) {
      return (
        <div className="h-5 w-full animate-pulse rounded bg-gray-200"></div>
      );
    }

    const selectedMethodId = checkoutForm.watch("customer.shipping_method.id");
    const selectedMethod = query.data?.cart.available_shipping_methods.find(
      (method) =>
        method.id === selectedMethodId ||
        method.id === query.data?.cart.shipping_method?.id,
    );

    if (!selectedMethod) return null;

    return (
      <div className="flex w-full cursor-pointer items-center text-sm font-medium text-gray-900">
        {selectedMethod.title} • {selectedMethod.price_label}
      </div>
    );
  };

  const renderCustomerInfo = () => {
    if (query.isLoading) {
      return (
        <div className="flex flex-col space-y-2">
          <div className="h-5 w-full animate-pulse rounded bg-gray-200"></div>
          <div className="h-5 w-full animate-pulse rounded bg-gray-200"></div>
        </div>
      );
    }
    if (!query.data?.cart?.email && !decodedJwt?.email) return null;

    return (
      <div className="flex w-full flex-col">
        {decodedJwt?.full_name ? (
          <div className="text-sm font-medium text-gray-900">
            {decodedJwt.full_name}
          </div>
        ) : null}
        <div className="text-sm text-gray-500">
          {query.data?.cart?.email ?? decodedJwt?.email}
        </div>
        <DropZone
          zone="below_contact"
          token={cartToken}
          subdomain={subdomain ?? null}
          page="checkout"
          companyId={query.data?.cart?.company?.id}
        />
      </div>
    );
  };

  const isDynamicPaymentLoading = selectedAlternatePaymentMethod
    ? isPaymentLoading(selectedAlternatePaymentMethod.type)
    : false;

  const isAnyLoading =
    query.isLoading ||
    checkoutLoading ||
    checkoutMutation.isPending ||
    isDynamicPaymentLoading;

  const isButtonLoading =
    isAnyLoading &&
    !query.isLoading &&
    !customerPaymentMethodsLoading &&
    !customerAddressesLoading;

  const getButtonText = useMemo(() => {
    if (isButtonLoading) {
      return `${t("processing_payment")}...`;
    }

    if (!requiresPaymentMethod) {
      return t("place_order", "Place order");
    }

    if (selectedAlternatePaymentMethod) {
      return `${t("pay_with")} ${selectedAlternatePaymentMethod.name}`;
    }

    return t("pay_now");
  }, [
    isButtonLoading,
    requiresPaymentMethod,
    selectedAlternatePaymentMethod,
    t,
  ]);

  // Payment method selection is kept clickable while a previous payment
  // method mutation is in flight (optimistic selection). The Pay Now
  // button still waits on isPaymentMethodMutationLoading.
  //
  // `valid_for_checkout_pre_payment` is the backend orchestrator's
  // pre-flight (orchestrator.rb#valid_for_checkout_pre_payment?). It
  // captures customer_limit, inventory, bundle, address, and email
  // failures; without gating here the customer could reach Pay, the
  // capture would then fail with a structured per-item error, and the
  // previous renderer surfaced it as "Items [object Object]".
  // Default-true keeps the button unaffected when the field is missing
  // (older API shapes / cached payloads).
  const isCartValidForCheckout =
    query.data?.cart?.valid_for_checkout_pre_payment !== false;
  const isPaymentMethodSelectionDisabled =
    isAnyLoading ||
    isSubscriptionPending ||
    isQuantityPending ||
    isDiscountPending ||
    isRemoveDiscountPending ||
    isAddressMutationLoading ||
    isUpdatingCountry ||
    isShippingMethodMutationLoading ||
    hasCartErrors(query.data?.cart ?? null) ||
    hasCartItemErrors(query.data?.cart ?? null) ||
    !isCartValidForCheckout;

  const isPayButtonDisabled =
    isPaymentMethodSelectionDisabled || isPaymentMethodMutationLoading;

  return (
    <div
      ref={scrollRef}
      className="relative z-35 col-span-4 flex-auto bg-white px-8 pt-0 pb-16 md:h-[94vh] md:overflow-y-auto"
    >
      <div
        className={cn("mx-auto max-w-lg lg:mx-0 lg:mr-auto", {
          "pointer-events-none opacity-60 select-none": isButtonLoading,
        })}
        aria-busy={isButtonLoading}
      >
        {cart?.points_enabled && (cart?.total_creditable_points ?? 0) > 0 && (
          <InfoAlert
            color={cart?.company?.checkout_primary_button_color ?? undefined}
            icon={<span>🎉</span>}
            className="my-6"
          >
            <p className="text-sm font-medium">
              {t("earning_points_on_order", undefined, {
                points: cart.total_creditable_points ?? 0,
                label: pointsLabel(
                  cart.total_creditable_points ?? 0,
                  cart.company,
                ),
              })}
            </p>
          </InfoAlert>
        )}
        <FastCheckoutOptions
          cartToken={cartToken}
          isLoading={query.isLoading}
          paymentMethodsIncludes={paymentMethodsIncludes}
          subdomain={subdomain ?? null}
          query={query}
          kountSessionId={kountSessionId}
        />
        <div className="mt-6 flex flex-row items-center justify-between border-b border-gray-300 pb-4">
          {renderCustomerInfo()}
        </div>
        {!query.data?.cart.digital_only && (
          <CustomerShippingAddresses
            cartToken={cartToken}
            jwt={jwt ?? ""}
            customerAddresses={customerAddresses?.addresses || []}
            customerAddressesLoading={customerAddressesLoading}
            selectedShippingAddressId={selectedShippingAddressId ?? null}
            handleSelect={handleSelectShippingAddress}
            countries={countries ?? []}
            networkError={addressError}
            paymentMethodsIncludes={paymentMethodsIncludes ?? null}
            cartCountry={query.data?.cart?.country?.name ?? undefined}
            cartIso={query.data?.cart?.country?.iso}
            query={query}
            isAddressMutationPending={
              isAddressMutationLoading || isUpdatingCountry
            }
          />
        )}
        {!query.data?.cart.digital_only && (
          <div className="w-full border-b border-gray-300 pb-4">
            <Accordion
              type="single"
              collapsible
              value={shippingMethodAccordionValue}
              onValueChange={setShippingMethodAccordionValue}
              className="w-full"
            >
              <AccordionItem value="shipping-method" className="border-b-0">
                <AccordionTrigger
                  data-testid="toggle-shipping-methods"
                  className="mt-4 mb-1 flex w-full items-center justify-between py-1 text-left hover:no-underline"
                >
                  <span className="text-sm text-gray-400">
                    {t("shipping_method")}
                  </span>
                </AccordionTrigger>

                {shippingMethodError && (
                  <div className="text-sm text-red-500">
                    {shippingMethodError}
                  </div>
                )}

                {!shippingMethodAccordionValue &&
                  renderCollapsedShippingMethod()}

                <AccordionContent className="px-0.5 pt-2">
                  <ShippingMethodSelect
                    isLoading={
                      query.isLoading ||
                      isQuantityPending ||
                      isAddressMutationLoading
                    }
                    control={control}
                    showLabel={false}
                    onSelect={onSelectShippingMethod}
                    options={query.data?.cart.available_shipping_methods || []}
                    addressId={query.data?.cart.ship_to?.id}
                    selectedShippingMethod={query.data?.cart.shipping_method}
                    isShippingMethodMutationPending={
                      isShippingMethodMutationLoading
                    }
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <DropZone
              zone="below_shipping_method"
              token={cartToken}
              subdomain={subdomain ?? null}
              page="checkout"
              companyId={query.data?.cart?.company?.id}
            />
          </div>
        )}

        {requiresPaymentMethod && !paymentMethodAutoAssignable && (
          <CustomerPaymentMethods
            customerPaymentMethods={
              customerPaymentMethods?.payment_methods || []
            }
            customerPaymentMethodsLoading={customerPaymentMethodsLoading}
            selectedPaymentMethodId={
              selectedPaymentMethodId
                ? selectedPaymentMethodId
                : (defaultPaymentMethod?.id ?? null)
            }
            handleSelect={handleSelectPaymentMethod}
            jwt={jwt ?? ""}
            cartToken={cartToken}
            defaultPaymentMethod={defaultPaymentMethod ?? null}
            networkError={paymentError}
            alternatePaymentMethods={alternatePaymentMethods ?? []}
            setSelectedAlternatePaymentMethod={
              setSelectedAlternatePaymentMethod
            }
            selectedAlternatePaymentMethod={selectedAlternatePaymentMethod}
            query={query}
            isPaymentMethodMutationPending={isPaymentMethodMutationLoading}
            disabled={isPaymentMethodSelectionDisabled}
            onCustomerDetailsChange={setCustomerDetails}
            customerDetailsValidationErrors={customerDetailsValidationErrors}
            onClearCustomerDetailsFieldError={(fieldKey) => {
              setCustomerDetailsValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[fieldKey];
                return newErrors;
              });
            }}
            customerDetailsInitialValues={customerDetailsInitialValues}
            checkoutFormControl={control}
            checkoutFormWatch={checkoutForm.watch}
            checkoutFormSetValue={checkoutForm.setValue}
            checkoutFormClearErrors={checkoutForm.clearErrors}
            countries={countries ?? []}
            paymentMethodsIncludes={paymentMethodsIncludes}
          />
        )}
        {cart?.points_enabled || cart?.company?.reward_points_enabled ? (
          <FluidPoints
            query={query}
            onApplyPoints={handleApplyPoints}
            onRemovePoints={handleRemovePoints}
            isLoading={applyPointsMutation.isPending}
            isRemoving={removePointsMutation.isPending}
          />
        ) : null}
        {!!cart?.company?.order_on_behalf && (
          <VolumeCard
            cvTotal={cart?.cv_total}
            qvTotal={cart?.qv_total}
            cartToken={cartToken}
            buyerRep={
              cart?.buyer_rep
                ? {
                    ...cart.buyer_rep,
                    image_url: cart.buyer_rep.image_url ?? undefined,
                  }
                : undefined
            }
            volumeRep={
              cart?.volume_rep
                ? {
                    ...cart.volume_rep,
                    image_url: cart.volume_rep.image_url ?? undefined,
                  }
                : undefined
            }
          />
        )}
        {optionalCheckboxAgreements.length > 0 && (
          <OptionalAgreements
            agreements={optionalCheckboxAgreements}
            onCheckedChange={setOptionalCheckedIds}
          />
        )}

        <MarketingCheckboxes
          collectEmailMarketing={
            cart?.company?.collect_email_marketing ?? false
          }
          collectSmsMarketing={cart?.company?.collect_sms_marketing ?? false}
          setEmailMarketing={setEmailMarketing}
          setSmsMarketing={setSmsMarketing}
        />

        {requiredAgreements.length > 0 && (
          <Agreements
            control={control}
            agreements={requiredAgreements}
            onToggle={() =>
              checkoutForm.trigger("customer.agreements_accepted")
            }
            cartToken={cartToken}
            subdomain={subdomain ?? null}
            companyId={query.data?.cart?.company?.id}
          />
        )}
        {/* Error Display Section */}
        <div className="space-y-3">
          {emailError && <ErrorAlert message={emailError} />}
          {addressError && (
            <ErrorAlert message={addressError} testId="address-error" />
          )}
          {shippingMethodError && <ErrorAlert message={shippingMethodError} />}
          {paymentError && (
            <ErrorAlert message={paymentError} testId="payment-error" />
          )}
          {checkoutError && (
            <ErrorAlert message={checkoutError} testId="checkout-error" />
          )}
          {hasCartErrors(query.data?.cart ?? null) && (
            <ErrorAlert
              message={(query.data?.cart?.messages?.errors || []).join(", ")}
            />
          )}
          {(query.data?.cart?.messages?.warnings ?? []).map(
            (message, index) => (
              <WarningAlert key={`cart-warning-${index}`} message={message} />
            ),
          )}
          {(query.data?.cart?.messages?.info ?? []).map((message, index) => (
            <MutedAlert key={`cart-info-${index}`} message={message} />
          ))}
          {/* The per-item-error component on the order summary already
              renders each specific message inline. The banner stays
              generic so the customer isn't shown the same text twice. */}
          {hasCartItemErrors(query.data?.cart ?? null) && (
            <ErrorAlert message={t("fix_cart_items_message")} />
          )}
          {/* Fallback when the backend rejected pre-payment but neither
              cart-level nor per-item error arrays carry a message — e.g.
              a bundle-level failure that flips
              valid_for_checkout_pre_payment without populating either
              source. Without this branch the Pay button is silently
              disabled with no indication of what to fix.

              Suppressed while the cart is "settling" — any in-flight
              mutation, an active cart refetch, or the customer
              addresses/payment methods queries still loading. CURRENT-1613
              flash logs showed the cart shipping with no ship_to and
              valid_for_checkout_pre_payment: false on first render of
              LoggedInCheckout, with no errors yet populated; the
              customer-payment-methods auto-select effect then fires
              updateCartPaymentMethod (no mutationKey, so it shows as
              `undefined` in useIsMutating), which the backend uses to
              attach ship_to/bill_to. Without these gates the banner
              flashes for ~1s before the cart stabilises. */}
          {!isCartValidForCheckout &&
            !hasCartErrors(query.data?.cart ?? null) &&
            !hasCartItemErrors(query.data?.cart ?? null) &&
            !query.isFetching &&
            !customerAddressesLoading &&
            !customerPaymentMethodsLoading &&
            !anyCartMutationPending && (
              <ErrorAlert message={t("fix_cart_items_message")} />
            )}
        </div>
        {selectedAlternatePaymentMethod?.type === "paypal" &&
        sdkType === "braintree" ? (
          <div className="space-y-3">
            {braintreePaypalBtn}
            {braintreeVenmoBtn}
            {braintreePayLaterBtn}
            {braintreePayLaterMsg}
          </div>
        ) : selectedAlternatePaymentMethod?.type === "paypal" &&
          (paypalButton || venmoButton || payLaterButton) ? (
          <div className="space-y-3">
            {paypalButton}
            {venmoButton}
            {payLaterButton}
          </div>
        ) : query.isLoading ? null : (
          <Button
            data-testid="submit-order"
            disabled={isPayButtonDisabled}
            onClick={handleSubmitPayment}
            className={cn(
              "h-input bg-brand-primary text-brand-primary-text relative z-20 mt-6 w-full rounded px-6 py-2 text-base font-semibold opacity-95 hover:opacity-100",
              {
                "mt-3":
                  emailError ||
                  addressError ||
                  shippingMethodError ||
                  paymentError ||
                  checkoutError ||
                  hasCartErrors(query.data?.cart ?? null) ||
                  hasCartItemErrors(query.data?.cart ?? null),
              },
            )}
          >
            {isButtonLoading && (
              <div className="mr-3 h-5 w-5 animate-spin rounded-full border-4 border-t-4 border-white border-t-transparent"></div>
            )}
            {getButtonText}
          </Button>
        )}
        <div className="flex w-full flex-row justify-center">
          <button
            onClick={() => logoutCustomerMutation.mutate({ cartToken })}
            className="relative z-20 my-4 text-xs text-gray-900"
            disabled={logoutCustomerMutation.isPending}
          >
            {logoutCustomerMutation.isPending ? (
              <div className="mr-3 h-5 w-5 animate-spin rounded-full border-4 border-t-4 border-gray-300 border-t-transparent"></div>
            ) : (
              t("check_out_as_guest")
            )}
          </button>
        </div>
        {footerAgreements.length > 0 && (
          <Footer
            isLoading={query.isLoading}
            companyName={query.data?.cart?.company?.name ?? ""}
            agreements={footerAgreements}
            className="py-4"
          />
        )}
      </div>

      {/* VGS 3DS Modal */}
      <VgsThreeDSModal
        isOpen={vgsThreeDSModal.isOpen}
        challengeHtml={vgsThreeDSModal.challengeHtml}
        challengeUrl={vgsThreeDSModal.challengeUrl}
        labels={{
          title: t("verify_your_payment"),
          cancel: t("cancel"),
          timedOut: t("payment_verification_timed_out"),
          stillWorking: t("still_working_with_your_bank"),
          dontRefresh: t("dont_refresh_this_tab"),
        }}
        onClose={(error?: string) => {
          const { merchantTxId } = vgsThreeDSModal;
          setVgsThreeDSModal({
            isOpen: false,
            challengeHtml: "",
            challengeUrl: "",
            transactionId: "",
            merchantTxId: "",
          });
          if (error) {
            setCheckoutLoading(false);
            setPaymentError(error);
            return;
          }
          // Customer abandoned the challenge: cancel the attempt server-side
          // so the cart leaves three_ds_pending now instead of at the T+6
          // safety net (kills the retry race - close used to be local-only).
          // Any failure falls back to today's local-only close so the
          // customer is never stranded. Neutral by contract: the backend
          // writes CANCELED, never three_ds_failed_at or an error message.
          if (!merchantTxId) {
            setCheckoutLoading(false);
            return;
          }
          // Hold Pay disabled while the cancel is in flight (bounded by the
          // call's 5s abort) so an instant re-Pay can't race the still-
          // pending cart into the backend's active-challenge guard.
          setCheckoutLoading(true);
          void cancelVgs3DS({ cartToken, merchantTxId })
            .catch((error: unknown) => {
              // Observability only — the local-only close is the designed
              // fallback and the safety net cleans the attempt at T+6.
              console.warn(
                "[VGS 3DS] cancel request failed; falling back to local-only close",
                error,
              );
            })
            .finally(() => {
              setCheckoutLoading(false);
              // Refresh so the UI reflects the cart back in payment_method
              // (mirrors the onError/onCanceled handlers).
              refetch();
            });
        }}
      />

      {/* BLIK OTP Modal */}
      <BlikOtpModal
        isOpen={blikOtpModal.isOpen}
        cartToken={cartToken}
        onClose={(error?: string) => {
          setBlikOtpModal({ isOpen: false });
          setCheckoutLoading(false);
          if (error) {
            setPaymentError(error);
          }
        }}
      />
    </div>
  );
}
